<template>
	<NuxtLayout>
		<div
			class="loading"
			v-if="baseStore.isLoading || mountLoad"
		>
			<div class="loading__lane"></div>
		</div>
		<UiLoaderLottieMobileBg v-if="config.public.isMobile" />
		<div v-else-if="mountLoad">
			<UiLoaderLottieBg />
		</div>
		<UiLoaderSystemUpdate
			v-if="isUpdating"
			:is-updating="isUpdating"
		/>
		<NotificationAlert
			:list="notificationStore.alerts"
			class="alert"
			@mouse-enter="mouseEnterAlert"
			@mouse-leave="mouseLeaveAlert"
		/>
		<NuxtPage />
		<VitePwaManifest v-if="isPwaEnabled" />
	</NuxtLayout>
</template>

<script setup lang="ts">
	import { TIMEDEAD_DEFAULT_FOR_ALERT } from '~/const/notification';
	import type { TAlert } from '~~/shared/interfaces/notifications';
	import { useBaseStore } from '~/store/base.store';
	import { useNotificationStore } from '~/store/notification.store';
	import { useProfileStore } from '~/store/profile.store';
	import 'virtual:svg-icons-register';
	import { useAppUpdater } from './composables/useAppUpdater';
	import { Capacitor } from '@capacitor/core';
	import { StatusBar, Style } from '@capacitor/status-bar';

	const isUpdating = ref(false);
	const baseStore = useBaseStore();
	const profile = useProfileStore();
	const notificationStore = useNotificationStore();
	baseStore.initializationApp();
	if (import.meta.client) {
		void profile.checkToken();
	}
	const mountLoad = ref(true);
	const config = useRuntimeConfig();
	const appUpdater = useAppUpdater();

	const mouseEnterAlert = (alert: TAlert) => {
		notificationStore.cancelAutomaticAlertRemoval(alert.id);
	};

	const mouseLeaveAlert = (alert: TAlert) => {
		notificationStore.deleteAlert(alert.id, TIMEDEAD_DEFAULT_FOR_ALERT);
	};

	const isPwaEnabled = computed(() => {
		return config.public.isPwaEnabled === 'true';
	});

	onMounted(async () => {
		mountLoad.value = false;
		if (!profile.profileInfo) {
			await profile.getProfile(true);
		}
		await profile.redirectToRoleHomePage();

		notificationStore.alerts.forEach((item) => {
			notificationStore.deleteAlert(item.id, 2000);
		});

		if (config.public.isMobile) {
			const stopWatch = watch(
				() => appUpdater.hasUpdate.value,
				(newVal) => {
					isUpdating.value = newVal;

					if (newVal === false) {
						stopWatch();
					}
				},
				{ immediate: false }
			);
			await appUpdater.initUpdater();
			if (appUpdater.hasUpdate.value === false) {
				stopWatch();
			}
			isUpdating.value = false;
		}

		if (Capacitor.isNativePlatform()) {
			try {
				await StatusBar.setBackgroundColor({ color: '#00000000' });
				await StatusBar.setStyle({ style: Style.Light });
				await StatusBar.setOverlaysWebView({ overlay: false });
			} catch (error) {
				console.error('StatusBar error:', error);
			}
		}
	});
</script>

<style scoped lang="scss">
	.loading {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 3px;
		z-index: 9999999;

		.loading__lane {
			position: absolute;
			width: 0;
			height: 4px;
			background: #2491e7;
			border-radius: 16px;
			animation: loading 2s ease-in-out infinite;

			&_lighter {
				background: #5bb7fd;
			}

			@media (max-width: $lg) {
				animation: loading-sm 2s ease-in-out infinite;
			}
		}
	}

	.alert {
		position: fixed;
		bottom: 10px;
		right: 10px;
		z-index: 10000000;
	}

	@keyframes loading {
		0% {
			width: 3%;
			left: 0;
		}
		50% {
			width: 6%;
		}
		100% {
			width: 2%;
			left: 100vw;
		}
	}
	@keyframes loading-sm {
		0% {
			width: 4%;
			left: 0;
		}
		50% {
			width: 10%;
		}
		100% {
			width: 3%;
			left: 100vw;
		}
	}
</style>
